import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { api } from "@/lib/api"
import { searchHeroContacts, kontaktName, empfaengerMitKontakt, type HeroContactHit } from "@/lib/heroSearch"
import { Paperclip, Send, X, Forward } from "lucide-react"
import { cn } from "@/lib/utils"
import { notifyPendingSend, type PendingSendInfo } from "@/lib/undoSend"
import { platzhalterAufloesen } from "@/lib/signaturBilder"

/**
 * Weiterleiten-Formular für eine einzelne E-Mail.
 * Sendet multipart/form-data an POST /admin/emails/api/email/{id}/forward.
 * - to, cc, subject, body
 * - attachment_ids (JSON-Array) für Original-Anhänge
 * - attachments[] (neue File-Uploads)
 */
export function ForwardForm({
  emailId,
  defaultSubject,
  defaultBody,
  originalAttachmentIds,
  originalHadAttachments,
  attachmentsLoading,
  tenantSlug,
  originalHtml,
  originalIsPlain,
  originalSender,
  originalDate,
  prefilledTo,
  openSignal,
}: {
  emailId: number
  defaultSubject: string
  defaultBody?: string
  originalAttachmentIds?: number[]
  originalHadAttachments?: boolean
  attachmentsLoading?: boolean
  tenantSlug?: string
  originalHtml?: string | null
  originalIsPlain?: boolean
  originalSender?: string
  originalDate?: string
  prefilledTo?: string
  openSignal?: number
}) {
  // Wenn nur Plain-Text da ist: escapen + als HTML mit klickbaren Links wrapen.
  const effectiveHtml = originalHtml
    ? (originalIsPlain ? plainToHtml(originalHtml) : originalHtml)
    : ""

  // Tenant-Signatur fuer Preview (Backend setzt sie via {{SIGNATURE}}-Platzhalter ein).
  const { data: tenantData } = useQuery({
    queryKey: ["tenant", tenantSlug || ""],
    queryFn: async () => {
      if (!tenantSlug) return null
      const { data } = await api.GET("/api/v1/tenants/{slug}", { params: { path: { slug: tenantSlug } } })
      return data || null
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  })
  const signatureHtml = tenantData?.signature_html || ""
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [to, setTo] = useState("")
  const [cc, setCc] = useState("")

  // Externer Quick-Action-Trigger (z.B. Forward-Rule-Button in der Liste):
  // openSignal aendert sich => Form aufklappen, defaultTo setzen, hochscrollen.
  useEffect(() => {
    if (openSignal && openSignal > 0) {
      setOpen(true)
      if (prefilledTo) setTo(prefilledTo)
      // kurz warten damit Form sicher gerendert ist, dann scrollen
      setTimeout(() => {
        const el = document.getElementById(`forward-form-${emailId}`)
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 50)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal])

  const initialSubject = defaultSubject?.toLowerCase().startsWith("fwd:")
    ? defaultSubject
    : `Fwd: ${defaultSubject || ""}`
  const [subject, setSubject] = useState(initialSubject)
  const [body, setBody] = useState(defaultBody || "")

  // BUGFIX 05.05.2026: useState initialisiert State nur beim ersten Mount.
  // Wenn der ForwardForm-Component zwischen Mails wiederverwendet wird
  // (gleicher Tree-Pfad, andere Props), behaelt der subject-State den Wert
  // der vorherigen Mail — das hat dazu gefuehrt dass Forwards mit dem
  // Subject einer ANDEREN Mail rausgegangen sind. emailId-Wechsel resettet.
  useEffect(() => {
    setSubject(initialSubject)
    setBody(defaultBody || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId])
  const [files, setFiles] = useState<File[]>([])
  const [includeOriginalAttachments, setIncludeOriginalAttachments] = useState(true)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [success, setSuccess] = useState(false)

  const m = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("to", to)
      fd.append("cc", cc)
      fd.append("subject", subject)
      fd.append("body", body)
      // HTML-Forward: User-Text + ursprüngliche Mail als HTML (inkl. Bilder/Layout)
      if (effectiveHtml) {
        fd.append("body_html", buildForwardHtml(body, effectiveHtml, originalSender, originalDate, defaultSubject))
      }
      if (includeOriginalAttachments && originalAttachmentIds?.length) {
        fd.append("attachment_ids", JSON.stringify(originalAttachmentIds))
      }
      for (const f of files) fd.append("attachments", f)
      // v1-Endpoint mit Sanity-Check (subject_mismatch). Der Server-Side-Block
      // (Bug 05.05.2026) gibt 400 mit detail.subject_mismatch=true zurueck.
      const send = async (force: boolean) => {
        if (force) fd.set("force_subject", "true")
        const r = await fetch(`/api/v1/emails/${emailId}/forward`, {
          method: "POST", body: fd, credentials: "include",
        })
        return r
      }
      let res = await send(false)
      if (res.status === 400) {
        try {
          const j = await res.json()
          const detail = j.detail || j
          if (detail?.subject_mismatch) {
            const ok = window.confirm(
              "Achtung: Betreff passt nicht zur weitergeleiteten Mail.\n\n" +
              `Original: ${detail.expected_contains || "?"}\n` +
              `Aktueller Betreff: ${detail.actual_subject || subject}\n\n` +
              "Trotzdem senden?",
            )
            if (!ok) throw new Error("Abgebrochen — Betreff korrigieren")
            res = await send(true)
          }
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message.startsWith("Abgebrochen"))
            throw parseErr
        }
      }
      if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try {
          const j = await res.json()
          msg = j.detail?.error || j.detail || j.error || msg
          if (typeof msg === "object") msg = JSON.stringify(msg)
        } catch { /* no-op */ }
        throw new Error(String(msg))
      }
      return res.json()
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
      setSuccess(true)
      setBody("")
      const recipient = to
      setTo("")
      setFiles([])
      // Phase 6.4 Soft-Send Undo-Toast
      try {
        notifyPendingSend(data as PendingSendInfo, `Weiterleitung an ${recipient}`)
      } catch { /* ignore */ }
      setTimeout(() => { setSuccess(false); setOpen(false) }, 2000)
    },
  })

  if (!open) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-md bg-secondary hover:bg-secondary/70"
        >
          <Forward className="w-3.5 h-3.5" /> Weiterleiten
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={(ev) => { ev.preventDefault(); m.mutate() }}
      className="mt-4 rounded-lg border bg-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold flex items-center gap-2">
          <Forward className="w-4 h-4" /> Weiterleiten
        </div>
        <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      <RecipientField label="An *" value={to} onChange={setTo} placeholder="empfänger@domain.de" required tenantSlug={tenantSlug || ""} />
      <RecipientField label="CC" value={cc} onChange={setCc} placeholder="(optional)" tenantSlug={tenantSlug || ""} />
      <Field label="Betreff" value={subject} onChange={setSubject} />

      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Nachricht</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          placeholder="Optionale Nachricht..."
          className="w-full rounded-md border bg-background px-3 py-2 text-[13.5px] font-sans leading-relaxed focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"
        />
      </div>

      {/* Anhang-Status: Checkbox wenn Anhaenge vorhanden, Warnung wenn nicht */}
      {originalAttachmentIds && originalAttachmentIds.length > 0 ? (
        <div>
          <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
            <input
              type="checkbox"
              checked={includeOriginalAttachments}
              onChange={(e) => setIncludeOriginalAttachments(e.target.checked)}
            />
            Original-Anhänge mitsenden ({originalAttachmentIds.length})
          </label>
          {!includeOriginalAttachments && (
            <div style={{ marginTop: 4, padding: "5px 8px", fontSize: 12, color: "#92400e", background: "#fef3c7", borderRadius: 4 }}>
              ⚠ Original-Anhänge werden NICHT mitgesendet
            </div>
          )}
        </div>
      ) : attachmentsLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>
          Anhänge werden geladen…
        </div>
      ) : originalHadAttachments ? (
        <div style={{ padding: "6px 10px", fontSize: 12, color: "#92400e", background: "#fef3c7", borderRadius: 4 }}>
          ⚠ Original-Anhänge konnten nicht geladen werden — bitte unten manuell anhängen
        </div>
      ) : null}

      {/* Signatur-Preview — Backend setzt sie zwischen Nachricht und Original ein */}
      {signatureHtml && (
        <div style={{ marginTop: 4 }}>
          <div className="text-[11px] text-muted-foreground" style={{ marginBottom: 4 }}>
            ✍ Signatur (wird automatisch angehaengt):
          </div>
          <div
            style={{
              padding: 8, borderRadius: 6, background: "#fafafa",
              border: "1px dashed var(--border-color)",
              fontSize: 12.5, color: "#334155", lineHeight: 1.5,
            }}
            dangerouslySetInnerHTML={{ __html: platzhalterAufloesen(signatureHtml, {
              slug: tenantSlug || "",
              logoSet: tenantData?.signature_logo_set,
              bilder: tenantData?.signature_images,
            }) }}
          />
        </div>
      )}

      {/* Original-Mail-Preview — wird als HTML mitgeschickt, damit der Empfaenger
          die Mail mit Bildern/Formatierung sieht. */}
      {effectiveHtml && (
        <div style={{ marginTop: 4 }}>
          <div className="text-[11px] text-muted-foreground" style={{ marginBottom: 4 }}>
            ↓ Wird weitergeleitet (Original-Mail vom {originalDate ? new Date(originalDate).toLocaleString("de-DE") : "—"} von {originalSender || "—"}):
            {originalIsPlain && <span style={{ marginLeft: 6, opacity: 0.7 }}>· (Plain-Text — HTML wird nachgeladen)</span>}
          </div>
          <iframe
            srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
              html,body{margin:0;padding:0;max-width:100%;overflow-x:hidden}
              body{padding:8px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;font-size:12.5px;color:#0f172a;overflow-wrap:anywhere!important;word-break:break-word!important}
              img{max-width:100%!important;height:auto!important}
              a{word-break:break-all!important;overflow-wrap:anywhere!important;display:inline-block;max-width:100%}
              pre,code{white-space:pre-wrap!important;word-break:break-all!important}
              table{max-width:100%!important;width:auto!important;table-layout:auto}
              td,th{max-width:100%!important;overflow-wrap:anywhere!important;word-break:break-word!important}
              div,p,span,li{max-width:100%!important;overflow-wrap:anywhere!important;word-break:break-word!important}
              *{box-sizing:border-box!important}
            </style></head><body>${effectiveHtml}</body></html>`}
            sandbox="allow-same-origin allow-popups"
            style={{
              width: "100%", height: 240, border: "1px solid var(--border-color)",
              borderRadius: 6, background: "#fff",
            }}
          />
        </div>
      )}

      <div>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-md bg-secondary hover:bg-secondary/70"
        >
          <Paperclip className="w-3.5 h-3.5" /> Zusätzlicher Anhang
        </button>
        {files.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-secondary px-2 py-0.5 rounded">
                {f.name}
                <button
                  type="button"
                  onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        {m.isError && (
          <span className="text-[12px] text-destructive">
            Fehler: {m.error instanceof Error ? m.error.message : "unbekannt"}
          </span>
        )}
        {success && (
          <span className="text-[12px] text-success font-semibold">✓ Weitergeleitet!</span>
        )}
        <button
          type="submit"
          disabled={m.isPending || !to}
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
            (m.isPending || !to) && "opacity-60 cursor-not-allowed",
          )}
        >
          <Send className="w-3.5 h-3.5" /> {m.isPending ? "Sendet..." : "Weiterleiten"}
        </button>
      </div>
    </form>
  )
}

/** Wandelt Plain-Text in HTML um: escapen, Zeilenumbrueche als <br>, Links klickbar. */
function plainToHtml(txt: string): string {
  const esc = txt.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  const linked = esc.replace(/(https?:\/\/[^\s<]+)/g, (u) =>
    `<a href="${u}" style="color:#2563eb;word-break:break-all">${u}</a>`,
  )
  return `<div style="white-space:pre-wrap;font-family:-apple-system,sans-serif;font-size:13px;line-height:1.5">${linked}</div>`
}

/** Baut den HTML-Body fuer Forward — User-Text oben, Trennlinie, Header-Block
 *  (Von/Datum/Betreff) und das Original-HTML eingebettet. So sieht der Empfaenger
 *  die Mail mit Bildern/Formatierung wie in Apple Mail / Outlook beim Forward. */
function buildForwardHtml(userText: string, originalHtml: string, sender?: string, date?: string, subject?: string): string {
  const escUser = userText
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>")
  const dateLabel = date ? new Date(date).toLocaleString("de-DE") : ""
  // {{SIGNATURE}}-Platzhalter zwischen User-Text und Original — Backend setzt Signatur ein.
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;color:#0f172a">
    <div>${escUser}</div>
    {{SIGNATURE}}
    <div style="border-top:1px solid #ddd;padding-top:10px;margin-top:10px">
      <div style="font-size:13px;color:#64748b;font-weight:600;margin-bottom:4px">---------- Weitergeleitete Nachricht ----------</div>
      ${sender ? `<div style="font-size:12px;color:#64748b"><b>Von:</b> ${sender.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>` : ""}
      ${dateLabel ? `<div style="font-size:12px;color:#64748b"><b>Datum:</b> ${dateLabel}</div>` : ""}
      ${subject ? `<div style="font-size:12px;color:#64748b"><b>Betreff:</b> ${subject.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>` : ""}
    </div>
    <div style="margin-top:14px">
      ${originalHtml}
    </div>
  </div>`
}

function Field({
  label, value, onChange, placeholder, required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
}) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-md border bg-background px-3 py-1.5 text-[13.5px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"
      />
    </div>
  )
}

/** Eingabefeld mit HERO-Kontakt-Autocomplete. */
function RecipientField({
  label, value, onChange, placeholder, required, tenantSlug,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
  tenantSlug: string
}) {
  const [hits, setHits] = useState<HeroContactHit[]>([])
  const [focused, setFocused] = useState(false)
  const debounceRef = useRef<number | null>(null)

  const handleChange = (v: string) => {
    onChange(v)
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    const q = v.trim()
    if (!tenantSlug || q.length < 2 || q.includes("@")) {
      setHits([])
      return
    }
    debounceRef.current = window.setTimeout(async () => {
      try {
        const r = await searchHeroContacts(tenantSlug, q)
        // Kontakte ohne E-Mail-Adresse sind als Empfaenger-Vorschlag nutzlos —
        // ein Klick darauf koennte gar nichts einsetzen.
        setHits(r.filter((c) => !!c.email).slice(0, 8))
      } catch {
        // Tippt der Nutzer gerade weiter, waere ein Fehlerbanner hier nur im
        // Weg — aber die alte Liste stehen zu lassen waere schlimmer: sie
        // gehoert zum vorigen Suchwort. Also leeren und still bleiben; der
        // Vorschlag ist eine Hilfe, kein Arbeitsschritt (#52).
        setHits([])
      }
    }, 250)
  }

  const pick = (c: HeroContactHit) => {
    // Ohne E-Mail-Adresse gibt es nichts einzusetzen. `kontaktName()` liefert
    // notfalls „Kontakt #123" — als Empfaenger waere das Unsinn, deshalb hier
    // nur der Anzeigename INNERHALB der Adresse.
    if (!c.email) { setHits([]); return }
    const name = kontaktName(c)
    const addr = `"${name}" <${c.email}>`
    onChange(empfaengerMitKontakt(value, addr))
    setHits([])
  }

  return (
    <div style={{ position: "relative" }}>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-md border bg-background px-3 py-1.5 text-[13.5px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"
      />
      {focused && hits.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "var(--bg-card)", border: "1px solid var(--border-color)",
          borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {hits.map((c) => {
            const name = kontaktName(c)
            return (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(c) }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "6px 10px", border: "none", background: "none",
                  cursor: "pointer", fontSize: 12.5,
                }}
                className="hover:bg-secondary"
              >
                <div style={{ fontWeight: 600 }}>{name}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                  {[c.email, c.phone].filter(Boolean).join(" · ")}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
